import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  useListInventoryPlans,
  useCreateInventoryPlan,
  useUpdateInventoryPlan,
  getListInventoryPlansQueryKey,
} from "@workspace/api-client-react";
import type { InventoryPlan } from "@workspace/api-client-react";

// Phase 10 — fixed-window passes that require validityDays when cadence=Non_Recurring
const FIXED_WINDOW_PASS_NAMES = ["Day_Pass", "Five_Days_Pass", "Week_Pass"] as const;

/** Human label for a plan's catalog billing cycle (inventory_plans.billing_cycle). */
const BILLING_CYCLE_LABELS: Record<string, string> = {
  daily: "Daily",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Half-yearly",
  annual: "Yearly",
};

const CreateInventoryPlanBody = z.object({
  planName: z.string().min(1, "Plan name is required"),
  monthlyPrice: z.number().min(0, "Price must be 0 or more"),
  billingCycle: z.enum(["daily", "monthly", "quarterly", "semiannual", "annual"]).optional(),
  description: z.string().nullable().optional(),
  status: z.enum(["Active", "Archived"]).nullable().optional(),
  // Phase 10 — cadence drives billing semantics + members tab filter
  cadence: z.enum(["Recurring", "Non_Recurring"]),
  // Phase 10 — fixed-window pass validity; required when planName is Day_Pass/Five_Days_Pass/One_Week_Pass
  validityDays: z.number().int().positive().nullable().optional(),
  durationMonths: z.number().int().nullable().optional(),
  securityDepositRequired: z.boolean().optional().default(false),
  discountPct: z.number().nullable().optional(),
  lateFeeAmount: z.number().nullable().optional(),
  lateFeeAfterDays: z.number().int().nullable().optional(),
}).superRefine((data, ctx) => {
  const isFixedWindowPass = (FIXED_WINDOW_PASS_NAMES as readonly string[]).includes(data.planName);
  if (isFixedWindowPass && (data.validityDays == null || data.validityDays <= 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["validityDays"],
      message: "Validity days is required for fixed-window passes (Day Pass / 5 Days Pass / Week Pass)",
    });
  }
});
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { plnId } from "@/lib/display-id";

type FormValues = z.infer<typeof CreateInventoryPlanBody>;

interface Props {
  openCreate: boolean;
  setOpenCreate: (open: boolean) => void;
}

export function PlansTab({ openCreate, setOpenCreate }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editItem, setEditItem] = useState<InventoryPlan | null>(null);

  const { data: plans, isLoading } = useListInventoryPlans();
  const createMutation = useCreateInventoryPlan();
  const updateMutation = useUpdateInventoryPlan();

  const form = useForm<FormValues>({
    resolver: zodResolver(CreateInventoryPlanBody),
    defaultValues: {
      planName: "",
      monthlyPrice: 0,
      billingCycle: "monthly",
      status: "Active",
      cadence: "Recurring",
      validityDays: null,
      durationMonths: null,
      securityDepositRequired: false,
      discountPct: null,
      lateFeeAmount: null,
      lateFeeAfterDays: null,
    },
  });

  const isOpen = openCreate || editItem !== null;
  const isEditMode = editItem !== null;

  // Phase 10 — cadence drives validityDays input enablement (Recurring disables it).
  const cadence = form.watch("cadence");
  const planName = form.watch("planName");
  const isFixedWindowPass = (FIXED_WINDOW_PASS_NAMES as readonly string[]).includes(planName ?? "");
  const validityDisabled = cadence === "Recurring";

  function openEdit(item: InventoryPlan) {
    setEditItem(item);
    const ext = item as unknown as {
      durationMonths?: number | null;
      securityDepositRequired?: boolean;
      discountPct?: number | null;
      lateFeeAmount?: number | null;
      lateFeeAfterDays?: number | null;
    };
    form.reset({
      planName: item.planName,
      monthlyPrice: item.monthlyPrice,
      billingCycle: item.billingCycle,
      description: item.description ?? "",
      status: item.status,
      cadence: item.cadence ?? "Recurring",
      validityDays: item.validityDays ?? null,
      durationMonths: ext.durationMonths ?? null,
      securityDepositRequired: ext.securityDepositRequired ?? false,
      discountPct: ext.discountPct ?? null,
      lateFeeAmount: ext.lateFeeAmount ?? null,
      lateFeeAfterDays: ext.lateFeeAfterDays ?? null,
    });
  }

  function handleClose() {
    setOpenCreate(false);
    setEditItem(null);
    form.reset();
  }

  async function onSubmit(values: FormValues) {
    try {
      if (isEditMode) {
        await updateMutation.mutateAsync({ id: editItem.id, data: values });
      } else {
        await createMutation.mutateAsync({ data: values });
      }
      toast({ title: "Plan saved." });
      queryClient.invalidateQueries({ queryKey: getListInventoryPlansQueryKey() });
      handleClose();
    } catch {
      toast({ title: "Something went wrong. Please try again.", variant: "destructive" });
    }
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            Plans{" "}
            <span className="text-muted-foreground font-normal text-sm">
              {plans ? `(${plans.length})` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Plan Name</TableHead>
                <TableHead>Monthly Price ₹</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Validity (days)</TableHead>
                <TableHead>Billing Cycle</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(8)].map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : plans?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                    No plans defined. Run the seed script to populate the 9 canonical Hoblix plans.
                  </TableCell>
                </TableRow>
              ) : (
                plans?.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-mono text-xs">{plnId(plan.id)}</TableCell>
                    <TableCell className="font-medium">{plan.planName.replace(/_/g, " ")}</TableCell>
                    <TableCell className="font-mono text-xs">₹{plan.monthlyPrice.toLocaleString()}</TableCell>
                    <TableCell>
                      <Badge variant={plan.cadence === "Recurring" ? "default" : "outline"}>
                        {plan.cadence === "Recurring" ? "Recurring" : "Non-Recurring"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {plan.validityDays != null ? plan.validityDays : "—"}
                    </TableCell>
                    <TableCell className="capitalize">{BILLING_CYCLE_LABELS[plan.billingCycle] ?? plan.billingCycle}</TableCell>
                    <TableCell>
                      <Badge variant={plan.status === "Active" ? "secondary" : "outline"}>
                        {plan.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(plan)}>
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isOpen} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditMode ? `Edit ${plnId(editItem!.id)}` : "Add Plan"}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
              <FormField
                name="planName"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Plan Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Hot_Desk" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="monthlyPrice"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Monthly Price ₹ <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="billingCycle"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Cycle</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="semiannual">Half-yearly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Phase 10 — cadence drives Recurring vs Non_Recurring billing semantics */}
              <FormField
                name="cadence"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cadence <span className="text-destructive">*</span></FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select cadence" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Recurring">Recurring (monthly billing)</SelectItem>
                        <SelectItem value="Non_Recurring">Non-Recurring (one-time or per-booking)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Workstation / Private_Cabin = Recurring. Day_Pass / passes / Meeting / Conference / Event / Shooting = Non_Recurring.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {/* Phase 10 — validityDays for fixed-window passes */}
              <FormField
                name="validityDays"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Validity Days
                      <span className="ml-2 text-xs text-muted-foreground">
                        (fixed-window passes only{isFixedWindowPass ? " — required" : ""})
                      </span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        placeholder={validityDisabled ? "Disabled for Recurring plans" : "e.g. 1 / 5 / 7"}
                        disabled={validityDisabled}
                        value={field.value ?? ""}
                        onChange={(e) =>
                          field.onChange(e.target.value === "" ? null : Number(e.target.value))
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="description"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="status"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value ?? "Active"} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Active">Active</SelectItem>
                        <SelectItem value="Archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="durationMonths"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (months)</FormLabel>
                    <Select onValueChange={(v) => field.onChange(Number(v))} value={field.value != null ? String(field.value) : ""}>
                      <FormControl>
                        <SelectTrigger><SelectValue placeholder="Select duration" /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="1">1 (Monthly)</SelectItem>
                        <SelectItem value="3">3 (Quarterly — schema only)</SelectItem>
                        <SelectItem value="6">6 (Six-Month — schema only)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="securityDepositRequired"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div>
                      <FormLabel className="text-base">Security Deposit Required</FormLabel>
                      <FormDescription>D-20 — collect 1mo rental deposit on member create</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="discountPct"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Discount % (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lateFeeAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Late Fee Amount (Rs., optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="0"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lateFeeAfterDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Late Fee After (days, optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="1"
                        placeholder="0"
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value === "" ? null : Number(e.target.value))}
                      />
                    </FormControl>
                    <FormDescription>D-23 — days after billing day before late fee fires</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Discard changes
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {createMutation.isPending || updateMutation.isPending ? "Saving…" : "Save Plan"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
